import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Container, Table, Button, Modal, Form, Tab, Tabs } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import axios from 'axios'
import V2Layout from '../components/V2Layout'
import './Admin.css'

const API = ''

const SKIP_COLS = ['id']
const CATEGORY_COL = 'category_id'

// Columns hidden from the edit form (data preserved in DB, replaced by lookup tables)
const HIDDEN_EDIT_COLS = [
    'image_url',
    'cryogenic',
    'inletConnections',
    'outletConnections',
    'minOperatingTemp',
    'maxOperatingTemp',
    'lineSize',
    'endConnections',
    'operatingTemperatures',
    'availableOperators',
    'fluidMedia',
    'body',
    'seals',
    'lockWire',
    'seatMaterial',
    'operator',
    'diaphragmMaterial',
    'seat',
    'sealMaterial',
    'crackingPressure',
    'flowCoefficient',
    'operatingPressures',
    'crackingPressures',
    'weight',
    'MEOP',
    'pressureRange',
    'sizes',
    'designPressures',
    'proofPressure',
    'burstPressure',
    'inletPressure',
    'outletPressure',
    'outletPressureRange',
    'outletSetpoint',
    'outletAdjustmentRange',
    'firstStagePressureRange',
    'firstStageSetpoint',
    'secondStagePressureRange',
    'secondStageSetpoint',
    'interstageSetpoint',
    'lineRegulation',
    'externalLeakage',
    'internalLeakage',
    'leakage',
    'fluidTemperature',
    'fluidTemperatureRange',
    'portTypes',
    'pressure',
    'model',
]

// Maps product table column names to their corresponding lookup table slugs
const FIELD_TO_LOOKUP = {
    lineSize: 'line_size',
    inletConnections: 'inlet_connections',
    outletConnections: 'outlet_connections',
    endConnections: 'end_connections',
    minOperatingTemp: 'min_operating_temp',
    maxOperatingTemp: 'max_operating_temp',
    body: 'body_material',
    seatMaterial: 'seat_material',
    seat: 'seat_material',
    sealMaterial: 'seal_material',
    seals: 'seal_material',
    fluidMedia: 'fluid_media',
    flowCoefficient: 'flow_coefficient',
    availableOperators: 'operators',
    operator: 'operators',
    portTypes: 'port_types',
    cryogenic: 'Cryogenic',
    productType: 'product_type',
    lockWire: 'lock_wire',
    diaphragmMaterial: 'diaphragm_material',
}

function colLabel(name) {
    if (name === 'sku') return 'SKU'
    if (name === 'MEOP') return 'MEOP'
    if (name === 'image_url') return 'Image URL (legacy)'
    if (name === 'category_id') return 'Category'
    return name.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase())
}

const ProductsAdminScreen = () => {
    const [products, setProducts] = useState([])
    const [categories, setCategories] = useState([])
    const [columns, setColumns] = useState([])
    const [filterCat, setFilterCat] = useState('')
    const [searchTerm, setSearchTerm] = useState('')

    const [showProductModal, setShowProductModal] = useState(false)
    const [editProduct, setEditProduct] = useState(null)
    const [productForm, setProductForm] = useState({})

    // Images for the product being edited
    const [productImages, setProductImages] = useState([])
    const [uploading, setUploading] = useState(false)
    const fileInputRef = useRef(null)

    // Attachments for the product being edited
    const [productAttachments, setProductAttachments] = useState([])
    const [uploadingAttachment, setUploadingAttachment] = useState(false)
    const attachmentInputRef = useRef(null)

    const [showCatModal, setShowCatModal] = useState(false)
    const [editCat, setEditCat] = useState(null)
    const [catForm, setCatForm] = useState({ name: '', parent_id: '', sort_order: '', description: '', icon: '' })

    // Custom Products - family cards state
    const [customFamilies, setCustomFamilies] = useState([])
    const [showFamilyModal, setShowFamilyModal] = useState(false)
    const [editFamily, setEditFamily] = useState(null)
    const [familyForm, setFamilyForm] = useState({ name: '', icon: '', icon_class: 'cc-icon-p', description: '', sort_order: '' })

    // Slideshow images for the family being edited
    const [familyImages, setFamilyImages] = useState([])
    const [uploadingFamilyImg, setUploadingFamilyImg] = useState(false)
    const familyFileInputRef = useRef(null)

    const [showDelete, setShowDelete] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState(null)

    // Lookup tables state
    const [lookupTables, setLookupTables] = useState([])
    const [lookupValues, setLookupValues] = useState([])
    const [selectedLookupTable, setSelectedLookupTable] = useState(null)
    const [tableValues, setTableValues] = useState([])
    const [newValueText, setNewValueText] = useState('')
    const [editingValue, setEditingValue] = useState(null)
    const [editValueText, setEditValueText] = useState('')
    const [showAddTable, setShowAddTable] = useState(false)
    const [editingTable, setEditingTable] = useState(false)
    const [editTableForm, setEditTableForm] = useState({ label: '', description: '' })
    const [draggedIdx, setDraggedIdx] = useState(null)
    const [dragOverIdx, setDragOverIdx] = useState(null)

    // Category lookup table mappings
    const [categoryLookupMappings, setCategoryLookupMappings] = useState({}) // { categoryId: [lookupTableId, ...] }
    const [selectedCatForMapping, setSelectedCatForMapping] = useState(null)
    const [newTableForm, setNewTableForm] = useState({ slug: '', label: '', description: '' })

    // End connection details
    const [endConnDetails, setEndConnDetails] = useState({}) // { lookupValueId: { type, typical_use, status } }
    // Seal material details
    const [sealMatDetails, setSealMatDetails] = useState({}) // { lookupValueId: { min_temp, max_temp } }
    // Lookup table product counts
    const [tableCounts, setTableCounts] = useState({}) // { tableId: productCount }

    // Product lookup values (checkboxes in edit modal)
    const [productLookupSelections, setProductLookupSelections] = useState([])

    const load = useCallback(() => {
        axios.get(`${API}/api/categories`).then(r => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => setCategories([]))
        axios.get(`${API}/api/products`).then(r => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]))
        axios.get(`${API}/api/lookup-tables`).then(r => setLookupTables(Array.isArray(r.data) ? r.data : [])).catch(() => setLookupTables([]))
        axios.get(`${API}/api/lookup-values`).then(r => setLookupValues(Array.isArray(r.data) ? r.data : [])).catch(() => setLookupValues([]))
        axios.get(`${API}/api/lookup-tables/counts`).then(r => setTableCounts(r.data || {})).catch(() => setTableCounts({}))
        axios.get(`${API}/api/custom-product-families`).then(r => setCustomFamilies(Array.isArray(r.data) ? r.data : [])).catch(() => setCustomFamilies([]))
        axios.get(`${API}/api/category-lookup-tables`).then(r => {
            const map = {}
            if (Array.isArray(r.data)) {
                r.data.forEach(row => {
                    if (!map[row.category_id]) map[row.category_id] = []
                    map[row.category_id].push(row.lookup_table_id)
                })
            }
            setCategoryLookupMappings(map)
        }).catch(() => setCategoryLookupMappings({}))
    }, [])

    useEffect(() => {
        load()
        axios.get(`${API}/api/product-columns`).then(r => setColumns(r.data.map(c => c.name))).catch(() => { })
    }, [load])

    const editableCols = useMemo(() => {
        const cols = columns.filter(c => !SKIP_COLS.includes(c) && !HIDDEN_EDIT_COLS.includes(c))
        // Ensure modelNumber appears right after name
        const priority = ['name', 'modelNumber']
        const prioritized = priority.filter(p => cols.includes(p))
        const rest = cols.filter(c => !priority.includes(c))
        return [...prioritized, ...rest]
    }, [columns])

    const categoryCounts = useMemo(() => {
        const counts = {}
        products.forEach(p => { if (p.category_id) counts[p.category_id] = (counts[p.category_id] || 0) + 1 })
        return counts
    }, [products])

    const filteredProducts = useMemo(() => {
        let result = products
        if (filterCat) {
            result = result.filter(p => p.category_id === +filterCat)
        }
        if (searchTerm.trim() !== '') {
            const query = searchTerm.toLowerCase().trim()
            result = result.filter(p => 
                (p.name && p.name.toLowerCase().includes(query)) ||
                (p.sku && p.sku.toLowerCase().includes(query)) ||
                (p.model && p.model.toLowerCase().includes(query)) ||
                (p.modelNumber && p.modelNumber.toLowerCase().includes(query))
            )
        }
        return result
    }, [products, filterCat, searchTerm])

    const blankForm = useMemo(() => {
        const f = {}
        editableCols.forEach(c => { f[c] = '' })
        return f
    }, [editableCols])

    // ── Load images for a product ──
    const loadImages = useCallback((productId) => {
        if (!productId) { setProductImages([]); return }
        axios.get(`${API}/api/products/${productId}/images`)
            .then(r => setProductImages(Array.isArray(r.data) ? r.data : []))
            .catch(() => setProductImages([]))
    }, [])

    // ── Load attachments for a product ──
    const loadAttachments = useCallback((productId) => {
        if (!productId) { setProductAttachments([]); return }
        axios.get(`${API}/api/products/${productId}/attachments`)
            .then(r => setProductAttachments(Array.isArray(r.data) ? r.data : []))
            .catch(() => setProductAttachments([]))
    }, [])

    // ── Load slideshow images for a custom product family ──
    const loadFamilyImages = useCallback((familyId) => {
        if (!familyId) { setFamilyImages([]); return }
        axios.get(`${API}/api/custom-product-families/${familyId}/images`)
            .then(r => setFamilyImages(Array.isArray(r.data) ? r.data : []))
            .catch(() => setFamilyImages([]))
    }, [])

    // ── Product CRUD ──
    const openAddProduct = () => {
        setEditProduct(null)
        setProductForm({ ...blankForm })
        setProductImages([])
        setProductAttachments([])
        setProductLookupSelections([])
        setShowProductModal(true)
    }
    const openEditProduct = (p) => {
        setEditProduct(p)
        const f = {}
        editableCols.forEach(c => { f[c] = p[c] != null ? String(p[c]) : '' })
        setProductForm(f)
        loadImages(p.id)
        loadAttachments(p.id)
        // Load lookup value associations
        axios.get(`${API}/api/products/${p.id}/lookup-values`)
            .then(r => setProductLookupSelections(r.data.map(v => v.value_id)))
            .catch(() => setProductLookupSelections([]))
        setShowProductModal(true)
    }
    const saveProduct = async () => {
        const data = { ...productForm, category_id: productForm.category_id || null }
        if (editProduct) {
            await axios.put(`${API}/api/products/${editProduct.id}`, data)
            await axios.put(`${API}/api/products/${editProduct.id}/lookup-values`, { values: productLookupSelections })
        } else {
            const res = await axios.post(`${API}/api/products`, data)
            // For new products, we'd need the ID to save lookup values - reload will handle it
            if (res.data && res.data.id) {
                await axios.put(`${API}/api/products/${res.data.id}/lookup-values`, { values: productLookupSelections })
            }
        }
        setShowProductModal(false)
        load()
    }

    // ── Image management ──
    const handleUpload = async (e) => {
        const files = e.target.files
        if (!files.length || !editProduct) return
        setUploading(true)
        const fd = new FormData()
        for (let i = 0; i < files.length; i++) fd.append('images', files[i])
        try {
            await axios.post(`${API}/api/products/${editProduct.id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            loadImages(editProduct.id)
        } catch (err) { console.error(err) }
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const deleteImage = async (imgId) => {
        await axios.delete(`${API}/api/product-images/${imgId}`)
        loadImages(editProduct.id)
    }

    const moveImage = async (imgId, direction) => {
        const idx = productImages.findIndex(i => i.id === imgId)
        if (idx < 0) return
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= productImages.length) return
        const a = productImages[idx], b = productImages[swapIdx]
        await Promise.all([
            axios.put(`${API}/api/product-images/${a.id}`, { sort_order: b.sort_order }),
            axios.put(`${API}/api/product-images/${b.id}`, { sort_order: a.sort_order })
        ])
        loadImages(editProduct.id)
    }

    // ── Attachment management ──
    const handleAttachmentUpload = async (e) => {
        const files = e.target.files
        if (!files.length || !editProduct) return
        setUploadingAttachment(true)
        const fd = new FormData()
        for (let i = 0; i < files.length; i++) fd.append('files', files[i])
        try {
            await axios.post(`${API}/api/products/${editProduct.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            loadAttachments(editProduct.id)
        } catch (err) { console.error(err) }
        setUploadingAttachment(false)
        if (attachmentInputRef.current) attachmentInputRef.current.value = ''
    }

    const deleteAttachment = async (attId) => {
        await axios.delete(`${API}/api/attachments/${attId}`)
        loadAttachments(editProduct.id)
    }

    // ── Category CRUD ──
    const openAddCat = () => { setEditCat(null); setCatForm({ name: '', parent_id: '', sort_order: '', description: '', icon: '' }); setShowCatModal(true) }
    const openEditCat = (c) => { setEditCat(c); setCatForm({ name: c.name || '', parent_id: c.parent_id || '', sort_order: c.sort_order !== undefined ? String(c.sort_order) : '0', description: c.description || '', icon: c.icon || '' }); setShowCatModal(true) }
    const saveCat = async () => {
        const data = { ...catForm, parent_id: catForm.parent_id || null, sort_order: catForm.sort_order !== '' ? +catForm.sort_order : 0 }
        if (editCat) await axios.put(`${API}/api/categories/${editCat.id}`, data)
        else await axios.post(`${API}/api/categories`, data)
        setShowCatModal(false)
        load()
    }

    // ── Custom Product Family CRUD ──
    const openAddFamily = () => { setEditFamily(null); setFamilyForm({ name: '', icon: '', icon_class: 'cc-icon-p', description: '', sort_order: String(customFamilies.length) }); setFamilyImages([]); setShowFamilyModal(true) }
    const openEditFamily = (f) => { setEditFamily(f); setFamilyForm({ name: f.name || '', icon: f.icon || '', icon_class: f.icon_class || 'cc-icon-p', description: f.description || '', sort_order: f.sort_order !== undefined ? String(f.sort_order) : '0' }); loadFamilyImages(f.id); setShowFamilyModal(true) }
    const saveFamily = async () => {
        const data = { ...familyForm, sort_order: familyForm.sort_order !== '' ? +familyForm.sort_order : 0 }
        if (editFamily) await axios.put(`${API}/api/custom-product-families/${editFamily.id}`, data)
        else await axios.post(`${API}/api/custom-product-families`, data)
        setShowFamilyModal(false)
        load()
    }

    // ── Family slideshow image management ──
    const handleFamilyImgUpload = async (e) => {
        const files = e.target.files
        if (!files.length || !editFamily) return
        setUploadingFamilyImg(true)
        const fd = new FormData()
        for (let i = 0; i < files.length; i++) fd.append('images', files[i])
        try {
            await axios.post(`${API}/api/custom-product-families/${editFamily.id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            loadFamilyImages(editFamily.id)
        } catch (err) { console.error(err) }
        setUploadingFamilyImg(false)
        if (familyFileInputRef.current) familyFileInputRef.current.value = ''
    }

    const deleteFamilyImg = async (imgId) => {
        await axios.delete(`${API}/api/custom-product-family-images/${imgId}`)
        loadFamilyImages(editFamily.id)
    }

    const moveFamilyImg = async (imgId, direction) => {
        const idx = familyImages.findIndex(i => i.id === imgId)
        if (idx < 0) return
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= familyImages.length) return
        const a = familyImages[idx], b = familyImages[swapIdx]
        await Promise.all([
            axios.put(`${API}/api/custom-product-family-images/${a.id}`, { sort_order: b.sort_order }),
            axios.put(`${API}/api/custom-product-family-images/${b.id}`, { sort_order: a.sort_order })
        ])
        loadFamilyImages(editFamily.id)
    }

    const moveCategory = async (cid, direction) => {
        const idx = categories.findIndex(c => c.id === cid)
        if (idx < 0) return
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= categories.length) return
        const a = categories[idx], b = categories[swapIdx]
        
        await Promise.all([
            axios.put(`${API}/api/categories/${a.id}`, { name: a.name, parent_id: a.parent_id, sort_order: b.sort_order || 0 }),
            axios.put(`${API}/api/categories/${b.id}`, { name: b.name, parent_id: b.parent_id, sort_order: a.sort_order || 0 })
        ])
        load()
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        if (deleteTarget.type === 'product') await axios.delete(`${API}/api/products/${deleteTarget.id}`)
        else if (deleteTarget.type === 'custom-family') await axios.delete(`${API}/api/custom-product-families/${deleteTarget.id}`)
        else await axios.delete(`${API}/api/categories/${deleteTarget.id}`)
        setShowDelete(false)
        setDeleteTarget(null)
        load()
    }

    const setField = (col, val) => {
        if (col === 'category_id' && editProduct && productLookupSelections.length > 0) {
            const confirmed = window.confirm(
                'Changing the category will keep your current attribute selections, but some may not be relevant to the new category. Do you want to continue?'
            )
            if (!confirmed) return
        }
        setProductForm(prev => ({ ...prev, [col]: val }))
    }

    // ── Lookup table helpers ──
    const loadTableValues = useCallback((tableId) => {
        axios.get(`${API}/api/lookup-tables/${tableId}/values`)
            .then(r => setTableValues(r.data))
            .catch(() => setTableValues([]))
    }, [])

    const selectLookupTable = (t) => {
        setSelectedLookupTable(t)
        loadTableValues(t.id)
        setNewValueText('')
        setEditingValue(null)
        setEditingTable(false)
        // Load end connection details if this is the end_connections table
        if (t.slug === 'end_connections') {
            axios.get(`${API}/api/end-connection-details`)
                .then(r => {
                    const map = {}
                    r.data.forEach(d => { map[d.lookup_value_id] = d })
                    setEndConnDetails(map)
                })
                .catch(() => setEndConnDetails({}))
        }
        // Load seal material details if this is the seal_material table
        if (t.slug === 'seal_material') {
            axios.get(`${API}/api/seal-material-details`)
                .then(r => {
                    const map = {}
                    r.data.forEach(d => { map[d.lookup_value_id] = d })
                    setSealMatDetails(map)
                })
                .catch(() => setSealMatDetails({}))
        }
    }

    const addValue = async () => {
        if (!newValueText.trim() || !selectedLookupTable) return
        await axios.post(`${API}/api/lookup-tables/${selectedLookupTable.id}/values`, { value: newValueText.trim(), sort_order: tableValues.length })
        setNewValueText('')
        loadTableValues(selectedLookupTable.id)
        load() // refresh global lookup values
    }

    const saveEditValue = async () => {
        if (!editingValue || !editValueText.trim()) return
        await axios.put(`${API}/api/lookup-values/${editingValue.id}`, { value: editValueText.trim(), sort_order: editingValue.sort_order })
        setEditingValue(null)
        setEditValueText('')
        loadTableValues(selectedLookupTable.id)
        load()
    }

    const deleteValue = async (vid) => {
        await axios.delete(`${API}/api/lookup-values/${vid}`)
        loadTableValues(selectedLookupTable.id)
        load()
    }

    const moveValue = async (vid, direction) => {
        const idx = tableValues.findIndex(v => v.id === vid)
        if (idx < 0) return
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= tableValues.length) return
        const a = tableValues[idx], b = tableValues[swapIdx]
        await Promise.all([
            axios.put(`${API}/api/lookup-values/${a.id}`, { value: a.value, sort_order: b.sort_order }),
            axios.put(`${API}/api/lookup-values/${b.id}`, { value: b.value, sort_order: a.sort_order })
        ])
        loadTableValues(selectedLookupTable.id)
    }

    const handleDragStart = (e, idx) => {
        setDraggedIdx(idx)
        e.dataTransfer.effectAllowed = 'move'
    }

    const handleDragOver = (e, idx) => {
        e.preventDefault()
        if (idx !== dragOverIdx) {
            setDragOverIdx(idx)
        }
    }

    const handleDrop = async (e, targetIdx) => {
        e.preventDefault()
        if (draggedIdx === null || draggedIdx === targetIdx) {
            setDraggedIdx(null)
            setDragOverIdx(null)
            return
        }

        const items = [...tableValues]
        const [draggedItem] = items.splice(draggedIdx, 1)
        items.splice(targetIdx, 0, draggedItem)

        // Assign new sort order based on position
        const updated = items.map((v, index) => ({ ...v, sort_order: index }))
        setTableValues(updated)

        // Reset drag states
        setDraggedIdx(null)
        setDragOverIdx(null)

        // Save order to DB
        try {
            await Promise.all(updated.map(item => 
                axios.put(`${API}/api/lookup-values/${item.id}`, { value: item.value, sort_order: item.sort_order })
            ))
            loadTableValues(selectedLookupTable.id)
            load()
        } catch (err) {
            console.error('Failed to reorder values:', err)
        }
    }

    const saveTableEdit = async () => {
        if (!editTableForm.label.trim()) return
        try {
            await axios.put(`${API}/api/lookup-tables/${selectedLookupTable.id}`, {
                label: editTableForm.label,
                description: editTableForm.description
            })
            setEditingTable(false)
            load()
            setSelectedLookupTable(prev => ({
                ...prev,
                label: editTableForm.label,
                description: editTableForm.description
            }))
        } catch (err) {
            console.error('Failed to save table edit:', err)
        }
    }

    const updateEndConnDetail = async (lookupValueId, field, value) => {
        const current = endConnDetails[lookupValueId] || { type: '', typical_use: '', status: 'Standard' }
        const updated = { ...current, [field]: value }
        setEndConnDetails(prev => ({ ...prev, [lookupValueId]: updated }))
        await axios.put(`${API}/api/end-connection-details/${lookupValueId}`, updated)
    }

    const updateSealMatDetail = async (lookupValueId, field, value) => {
        const current = sealMatDetails[lookupValueId] || { min_temp: '', max_temp: '' }
        const updated = { ...current, [field]: value }
        setSealMatDetails(prev => ({ ...prev, [lookupValueId]: updated }))
        await axios.put(`${API}/api/seal-material-details/${lookupValueId}`, updated)
    }

    const addLookupTable = async () => {
        if (!newTableForm.slug.trim() || !newTableForm.label.trim()) return
        await axios.post(`${API}/api/lookup-tables`, newTableForm)
        setShowAddTable(false)
        setNewTableForm({ slug: '', label: '', description: '' })
        load()
    }

    const deleteLookupTable = async (tid) => {
        if (!window.confirm('Delete this lookup table and all its values?')) return
        await axios.delete(`${API}/api/lookup-tables/${tid}`)
        if (selectedLookupTable?.id === tid) { setSelectedLookupTable(null); setTableValues([]) }
        load()
    }

    // Toggle a lookup value checkbox for the product being edited
    const toggleLookupValue = (valueId) => {
        setProductLookupSelections(prev =>
            prev.includes(valueId) ? prev.filter(v => v !== valueId) : [...prev, valueId]
        )
    }

    // Group lookup values by table for the product edit modal - filtered by selected category
    const lookupValuesByTable = useMemo(() => {
        const grouped = {}
        const tables = Array.isArray(lookupTables) ? lookupTables : []
        const values = Array.isArray(lookupValues) ? lookupValues : []
        const selectedCatId = productForm.category_id ? +productForm.category_id : null
        const allowedTableIds = selectedCatId && categoryLookupMappings[selectedCatId]
            ? new Set(categoryLookupMappings[selectedCatId])
            : null // null = show all (no category selected or no mapping defined)

        tables.forEach(t => {
            if (allowedTableIds === null || allowedTableIds.has(t.id)) {
                grouped[t.slug] = { table: t, values: [] }
            }
        })
        values.forEach(v => {
            if (grouped[v.slug]) grouped[v.slug].values.push(v)
        })
        return grouped
    }, [lookupTables, lookupValues, productForm.category_id, categoryLookupMappings])

    return (
        <V2Layout pageClass="pfc-products">
            <style>{`
                /* Admin Layout Polish */
                .pfc-admin-container {
                    padding: 3rem 0 5rem;
                    background-color: #faf9f6;
                    min-height: 80vh;
                }
                .pfc-admin-card {
                    background: #fff;
                    border: 1px solid #e5e3dd;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
                    padding: 32px;
                    margin-bottom: 24px;
                }
                
                /* Custom Tabs styling */
                .pfc-admin-tabs.nav-pills {
                    border-bottom: 1px solid #e5e3dd;
                    padding-bottom: 12px;
                    margin-bottom: 24px;
                }
                .pfc-admin-tabs.nav-pills .nav-link {
                    font-family: var(--pfc-mono, monospace);
                    font-size: 13px;
                    font-weight: 600;
                    color: #5F5E5A;
                    background: none;
                    border: 1px solid transparent;
                    padding: 8px 16px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .pfc-admin-tabs.nav-pills .nav-link:hover {
                    color: #26215C;
                    background-color: rgba(38, 33, 148, 0.04);
                }
                .pfc-admin-tabs.nav-pills .nav-link.active {
                    color: #fff !important;
                    background-color: #26215C !important;
                    box-shadow: 0 4px 12px rgba(38, 33, 92, 0.2);
                }
                
                /* Table Polish */
                .pfc-admin-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    border: 1px solid #e5e3dd;
                    border-radius: 6px;
                    overflow: hidden;
                    background-color: #fff;
                }
                .pfc-admin-table thead th {
                    background-color: #26215C !important;
                    color: #fff !important;
                    font-family: var(--pfc-mono, monospace);
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    font-weight: 600;
                    border: none;
                    padding: 12px 16px;
                }
                .pfc-admin-table tbody td {
                    padding: 12px 16px;
                    vertical-align: middle;
                    font-size: 14px;
                    color: #333;
                    border-bottom: 1px solid #f1efe8;
                }
                .pfc-admin-table tbody tr:last-child td {
                    border-bottom: none;
                }
                .pfc-admin-table tbody tr {
                    transition: background-color 0.15s ease;
                }
                .pfc-admin-table tbody tr:hover {
                    background-color: rgba(38, 33, 148, 0.01) !important;
                }
                
                /* Search / Controls Row */
                .pfc-controls-row {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                }
                .pfc-search-input {
                    max-width: 320px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 14px;
                    padding: 6px 12px;
                    transition: border-color 0.15s ease-in-out;
                }
                .pfc-search-input:focus {
                    border-color: #26215C;
                    box-shadow: 0 0 0 0.2rem rgba(38, 33, 92, 0.15);
                }
                
                /* Side Panels */
                .pfc-admin-side-list {
                    border: 1px solid #e5e3dd;
                    border-radius: 6px;
                    overflow: hidden;
                    background: #fff;
                }
                .pfc-admin-side-item {
                    padding: 10px 14px;
                    cursor: pointer;
                    font-size: 13px;
                    border-bottom: 1px solid #e5e3dd;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    transition: all 0.15s ease;
                    color: #444;
                }
                .pfc-admin-side-item:last-child {
                    border-bottom: none;
                }
                .pfc-admin-side-item:hover {
                    background-color: #faf9f6;
                    color: #26215C;
                }
                .pfc-admin-side-item.active {
                    background-color: rgba(38, 33, 92, 0.08);
                    color: #26215C;
                    font-weight: 600;
                    border-left: 3px solid #26215C;
                    padding-left: 11px; /* Adjust padding due to border */
                }
                
                /* Custom Checkbox card layout (Radix/Glassmorphic style) */
                .pfc-checkbox-card input[type="checkbox"] {
                    display: none;
                }
                .pfc-checkbox-card {
                    display: flex;
                    align-items: center;
                    position: relative;
                    padding: 10px 14px;
                    border-radius: 6px;
                    border: 1px solid #e5e3dd;
                    background-color: #fff;
                    font-size: 13px;
                    font-weight: 500;
                    color: #555;
                    cursor: pointer;
                    user-select: none;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .pfc-checkbox-card:hover {
                    border-color: #26215C;
                    background-color: rgba(38, 33, 92, 0.02);
                }
                .pfc-checkbox-card::before {
                    content: "";
                    width: 14px;
                    height: 14px;
                    border: 1px solid #ced4da;
                    border-radius: 3px;
                    margin-right: 8px;
                    background-color: #fff;
                    display: inline-block;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                .pfc-checkbox-card.checked {
                    border-color: #26215C;
                    background-color: rgba(38, 33, 92, 0.04);
                    color: #26215C;
                    font-weight: 600;
                }
                .pfc-checkbox-card.checked::before {
                    background-color: #26215C;
                    border-color: #26215C;
                    content: "✓";
                    color: #fff;
                    font-size: 10px;
                    line-height: 12px;
                    text-align: center;
                    font-weight: bold;
                }

                /* Stats Dashboard */
                .pfc-stats-panel {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 32px;
                }
                .pfc-stat-card {
                    background: #fff;
                    border: 1px solid #e5e3dd;
                    border-radius: 6px;
                    padding: 16px 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.01);
                    border-left: 4px solid #26215C;
                    display: flex;
                    flex-direction: column;
                }
                .pfc-stat-card.uncategorized {
                    border-left-color: #e09b3d;
                }
                .pfc-stat-label {
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #888;
                    margin-bottom: 4px;
                    font-family: var(--pfc-mono, monospace);
                    font-weight: 600;
                }
                .pfc-stat-value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #26215C;
                    font-family: var(--pfc-mono, monospace);
                }

                /* Thumbnail column */
                .pfc-thumbnail-wrapper {
                    width: 44px;
                    height: 44px;
                    border-radius: 4px;
                    border: 1px solid #e5e3dd;
                    overflow: hidden;
                    background-color: #f5f5f5;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .pfc-thumbnail {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
                .pfc-thumbnail-placeholder {
                    font-size: 18px;
                    color: #aaa;
                    user-select: none;
                }

                /* Media Indicator badges */
                .pfc-media-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 11px;
                    font-weight: 500;
                    color: #888;
                    background-color: #fcfbfa;
                    padding: 3px 8px;
                    border-radius: 12px;
                    border: 1px solid #e5e3dd;
                    transition: all 0.15s ease;
                }
                .pfc-media-badge.has-items {
                    background-color: rgba(38, 33, 92, 0.04);
                    border-color: rgba(38, 33, 92, 0.12);
                    color: #26215C;
                }
                .pfc-media-badge:hover {
                    background-color: rgba(38, 33, 92, 0.06);
                }

                /* Spec selection chips inside edit modal */
                .pfc-spec-chip-container {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-top: 4px;
                }
                .pfc-spec-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 11px;
                    font-weight: 500;
                    color: #555;
                    background-color: #fff;
                    border: 1px solid #ced4da;
                    padding: 3px 10px;
                    border-radius: 12px;
                    cursor: pointer;
                    user-select: none;
                    transition: all 0.15s ease;
                }
                .pfc-spec-chip input[type="checkbox"] {
                    display: none;
                }
                .pfc-spec-chip:hover {
                    border-color: #26215C;
                    background-color: rgba(38, 33, 92, 0.02);
                }
                .pfc-spec-chip.active {
                    background-color: #26215C;
                    border-color: #26215C;
                    color: #fff;
                    font-weight: 600;
                }

                /* Image upload zone */
                .pfc-upload-zone {
                    border: 2px dashed #ced4da;
                    border-radius: 6px;
                    padding: 24px;
                    text-align: center;
                    background-color: #faf9f6;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    position: relative;
                }
                .pfc-upload-zone:hover {
                    border-color: #26215C;
                    background-color: rgba(38, 33, 92, 0.02);
                }
                .pfc-upload-zone input[type="file"] {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                    cursor: pointer;
                }

                /* Attachment file item */
                .pfc-file-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    border: 1px solid #e5e3dd;
                    border-radius: 6px;
                    background-color: #fff;
                    margin-bottom: 8px;
                    transition: all 0.15s ease;
                }
                .pfc-file-item:hover {
                    border-color: #26215C;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.02);
                }
                .pfc-file-name {
                    font-size: 13px;
                    font-weight: 500;
                    color: #26215C;
                    text-decoration: none;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .pfc-file-name:hover {
                    text-decoration: underline;
                }
                .pfc-image-tile:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                }
            `}</style>
            <section className="pfc-admin-container">
                <Container>
                    <div className="pfc-admin-card">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                            <div>
                                <h1 style={{ color: '#26215C', fontSize: '2rem', fontWeight: 700, margin: 0 }}>Products Admin</h1>
                                <p className="text-muted mb-0 mt-1" style={{ fontSize: '13px' }}>
                                    Manage website product catalog, categories, technical properties, and custom specifications.
                                </p>
                            </div>
                        </div>

                        {/* Top sub-navigation bar */}
                        <div className="d-flex gap-3 mb-4 align-items-center pfc-admin-subnav">
                            <Link to="/admin" className="pfc-admin-subnav-link">Dashboard</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/products/admin" className="pfc-admin-subnav-link active">Products Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/capabilities/admin" className="pfc-admin-subnav-link">Capabilities Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/news/admin" className="pfc-admin-subnav-link">News Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/story/admin" className="pfc-admin-subnav-link">Timeline Admin</Link>
                            <span className="pfc-admin-subnav-sep">|</span>
                            <Link to="/certifications/admin" className="pfc-admin-subnav-link">Certifications Admin</Link>
                        </div>

                        {/* Statistics Dashboard Panel */}
                        <div className="pfc-stats-panel">
                            <div className="pfc-stat-card">
                                <span className="pfc-stat-label">Total Products</span>
                                <span className="pfc-stat-value">{products.length}</span>
                            </div>
                            <div className="pfc-stat-card">
                                <span className="pfc-stat-label">Categories</span>
                                <span className="pfc-stat-value">{categories.length}</span>
                            </div>
                            <div className="pfc-stat-card">
                                <span className="pfc-stat-label">Lookup Tables</span>
                                <span className="pfc-stat-value">{lookupTables.length}</span>
                            </div>
                            <div className="pfc-stat-card uncategorized">
                                <span className="pfc-stat-label">Uncategorized</span>
                                <span className="pfc-stat-value">{products.filter(p => !p.category_id).length}</span>
                            </div>
                        </div>

                        <Tabs defaultActiveKey="products" className="mb-3 pfc-admin-tabs" variant="pills">
                            <Tab eventKey="products" title="Products">
                                <div className="pfc-controls-row">
                                    <Form.Control
                                        type="text"
                                        placeholder="🔍 Search products by name, SKU, model..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className="pfc-search-input"
                                    />
                                    <Form.Select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ maxWidth: '220px' }}>
                                        <option value="">All Categories</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </Form.Select>
                                    <Button size="sm" onClick={openAddProduct} className="pfc-admin-btn-primary" style={{ padding: '6px 16px', fontWeight: 500 }}>+ Add Product</Button>
                                </div>
                                <Table bordered hover responsive className="pfc-admin-table">
                                    <thead><tr>
                                        <th style={{ width: '60px' }}>Image</th>
                                        <th>Model #</th>
                                        <th>Name</th>
                                        <th>SKU</th>
                                        <th>Category</th>
                                        <th style={{ width: '120px' }}>Media</th>
                                        <th style={{ width: '140px' }}>Actions</th>
                                    </tr></thead>
                                    <tbody>
                                        {filteredProducts.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} style={{ padding: '48px 16px', textAlign: 'center', color: '#888' }}>
                                                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🔍</div>
                                                    <strong>No products found</strong>
                                                    <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Try adjusting your search query or category filter.</p>
                                                    {(searchTerm || filterCat) && (
                                                        <Button size="sm" variant="link" onClick={() => { setSearchTerm(''); setFilterCat('') }} style={{ color: '#26215C', fontWeight: 600, marginTop: '8px', textDecoration: 'none' }}>
                                                            Reset Filters
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredProducts.map(p => (
                                                <tr key={p.id}>
                                                    <td>
                                                        <div className="pfc-thumbnail-wrapper">
                                                            {p.primary_image ? (
                                                                <img
                                                                    src={`${API}/products/images/product/${p.primary_image}`}
                                                                    alt={p.name}
                                                                    className="pfc-thumbnail"
                                                                />
                                                            ) : (
                                                                <span className="pfc-thumbnail-placeholder">📦</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontFamily: 'var(--pfc-mono, monospace)', fontWeight: 600 }}>{p.model || p.modelNumber || '-'}</td>
                                                    <td style={{ fontWeight: 500 }}>{p.name}</td>
                                                    <td style={{ fontFamily: 'var(--pfc-mono, monospace)', fontSize: '12px' }}>{p.sku}</td>
                                                    <td>
                                                        {p.category_name ? (
                                                            <span className="pfc-badge pfc-badge-category">
                                                                {p.category_name}
                                                            </span>
                                                        ) : (
                                                            <span className="pfc-badge pfc-badge-uncategorized">
                                                                Uncategorized
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <span className={`pfc-media-badge ${p.image_count > 0 ? 'has-items' : ''}`} title={`${p.image_count || 0} image(s)`}>
                                                                🖼️ {p.image_count || 0}
                                                            </span>
                                                            <span className={`pfc-media-badge ${p.attachment_count > 0 ? 'has-items' : ''}`} title={`${p.attachment_count || 0} attachment(s)`}>
                                                                📄 {p.attachment_count || 0}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Button size="sm" variant="outline-primary" onClick={() => openEditProduct(p)} style={{ marginRight: '0.5rem', padding: '3px 8px', fontSize: '12px' }}>Edit</Button>
                                                        <Button size="sm" variant="outline-danger" onClick={() => { setDeleteTarget({ type: 'product', id: p.id, name: p.name }); setShowDelete(true) }} style={{ padding: '3px 8px', fontSize: '12px' }}>Del</Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </Tab>

                            <Tab eventKey="categories" title="Categories">
                                <Button size="sm" onClick={openAddCat} className="pfc-admin-btn-primary" style={{ marginBottom: '1.5rem', padding: '6px 16px', fontWeight: 500 }}>+ Add Category</Button>
                                <Table bordered hover responsive className="pfc-admin-table">
                                    <thead><tr><th>Name</th><th>Sort Order</th><th>Products</th><th style={{ width: '220px' }}>Actions</th></tr></thead>
                                    <tbody>
                                        {categories.map((c, idx) => (
                                            <tr key={c.id}>
                                                <td>{c.icon && <i className={`ti ${c.icon}`} style={{ marginRight: '0.5rem', color: '#888' }}></i>}{c.name}</td>
                                                <td style={{ fontSize: '0.85rem', color: '#888' }}>{c.sort_order || 0}</td>
                                                <td>{categoryCounts[c.id] || 0}</td>
                                                <td>
                                                    <Button size="sm" variant="outline-secondary" onClick={() => moveCategory(c.id, 'up')} disabled={idx === 0} style={{ marginRight: '0.25rem', padding: '0.25rem 0.5rem', fontWeight: 'bold' }}>▲</Button>
                                                    <Button size="sm" variant="outline-secondary" onClick={() => moveCategory(c.id, 'down')} disabled={idx === categories.length - 1} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem', fontWeight: 'bold' }}>▼</Button>
                                                    <Button size="sm" variant="outline-primary" onClick={() => openEditCat(c)} style={{ marginRight: '0.5rem' }}>Edit</Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => { setDeleteTarget({ type: 'category', id: c.id, name: c.name }); setShowDelete(true) }}>Del</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Tab>

                            <Tab eventKey="lookups" title="Lookup Tables">
                                <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.5rem', minHeight: '400px' }}>
                                    {/* Left: table list */}
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: '0.85rem' }}>Tables</strong>
                                            <Button size="sm" variant="outline-secondary" onClick={() => setShowAddTable(true)}>+ New</Button>
                                        </div>
                                        <div className="pfc-admin-side-list">
                                            {lookupTables.map(t => (
                                                <div
                                                    key={t.id}
                                                    onClick={() => selectLookupTable(t)}
                                                    className={`pfc-admin-side-item ${selectedLookupTable?.id === t.id ? 'active' : ''}`}
                                                >
                                                    <span>
                                                        {t.label}
                                                        {tableCounts[t.id] > 0 && (
                                                            <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 600, color: 'rgb(150, 120, 230)', background: 'rgba(38,33,92,0.08)', padding: '1px 6px', borderRadius: '10px' }}>
                                                                {tableCounts[t.id]}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <Button size="sm" variant="link" style={{ color: '#dc3545', padding: 0, fontSize: '0.75rem', textDecoration: 'none' }} onClick={(e) => { e.stopPropagation(); deleteLookupTable(t.id) }}>×</Button>
                                                </div>
                                            ))}
                                        </div>
                                        {showAddTable && (
                                            <div style={{ marginTop: '0.75rem', border: '1px solid #ddd', borderRadius: '4px', padding: '0.75rem' }}>
                                                <Form.Control size="sm" placeholder="Slug (e.g. body_material)" className="mb-2" value={newTableForm.slug} onChange={e => setNewTableForm({ ...newTableForm, slug: e.target.value })} />
                                                <Form.Control size="sm" placeholder="Label (e.g. Body Material)" className="mb-2" value={newTableForm.label} onChange={e => setNewTableForm({ ...newTableForm, label: e.target.value })} />
                                                <Form.Control size="sm" placeholder="Description (optional)" className="mb-2" value={newTableForm.description} onChange={e => setNewTableForm({ ...newTableForm, description: e.target.value })} />
                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                    <Button size="sm" className="pfc-admin-btn-primary" onClick={addLookupTable}>Add</Button>
                                                    <Button size="sm" variant="secondary" onClick={() => setShowAddTable(false)}>Cancel</Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right: values for selected table */}
                                    <div>
                                        {selectedLookupTable ? (
                                            <>
                                                {editingTable ? (
                                                    <div style={{ border: '1px solid #e5e3dd', borderRadius: '6px', padding: '1rem', marginBottom: '1.25rem', backgroundColor: '#faf9f6' }}>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#26215C' }}>Table Label</Form.Label>
                                                            <Form.Control size="sm" placeholder="Label (e.g. Body Material)" value={editTableForm.label} onChange={e => setEditTableForm({ ...editTableForm, label: e.target.value })} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#26215C' }}>Table Description</Form.Label>
                                                            <Form.Control size="sm" placeholder="Description (optional)" value={editTableForm.description} onChange={e => setEditTableForm({ ...editTableForm, description: e.target.value })} />
                                                        </Form.Group>
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <Button size="sm" className="pfc-admin-btn-primary" onClick={saveTableEdit}>Save</Button>
                                                            <Button size="sm" variant="secondary" onClick={() => setEditingTable(false)}>Cancel</Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', borderBottom: '1px solid #e5e3dd', paddingBottom: '0.75rem' }}>
                                                        <div>
                                                            <h5 style={{ fontSize: '1.1rem', marginBottom: '0.25rem', color: '#26215C', fontWeight: 600 }}>{selectedLookupTable.label}</h5>
                                                            {selectedLookupTable.description && <p style={{ fontSize: '0.8rem', color: '#666', margin: 0 }}>{selectedLookupTable.description}</p>}
                                                        </div>
                                                        <Button size="sm" variant="outline-secondary" style={{ fontSize: '11px', fontFamily: 'var(--pfc-mono, monospace)', textTransform: 'uppercase', fontWeight: 600 }} onClick={() => { setEditingTable(true); setEditTableForm({ label: selectedLookupTable.label, description: selectedLookupTable.description || '' }) }}>Rename / Edit Table</Button>
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
                                                    <Form.Control size="sm" placeholder="Add new value..." value={newValueText} onChange={e => setNewValueText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addValue()} style={{ maxWidth: '300px' }} />
                                                    <Button size="sm" className="pfc-admin-btn-primary" style={{ padding: '4px 14px' }} onClick={addValue}>Add</Button>
                                                </div>

                                                <Table bordered size="sm" hover className="pfc-admin-table">
                                                    <thead><tr>
                                                        <th style={{ width: '40px', textAlign: 'center' }}>↕</th>
                                                        <th>Value</th>
                                                        {selectedLookupTable?.slug === 'end_connections' && <th>Type</th>}
                                                        {selectedLookupTable?.slug === 'end_connections' && <th>Typical Use</th>}
                                                        {selectedLookupTable?.slug === 'end_connections' && <th style={{ width: '120px' }}>Status</th>}
                                                        {selectedLookupTable?.slug === 'seal_material' && <th>Min Temp</th>}
                                                        {selectedLookupTable?.slug === 'seal_material' && <th>Max Temp</th>}
                                                        <th style={{ width: '80px' }}>Order</th>
                                                        <th style={{ width: '180px' }}>Actions</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {tableValues.map((v, vIdx) => {
                                                            const isDragged = draggedIdx === vIdx
                                                            const isDragOver = dragOverIdx === vIdx
                                                            return (
                                                                <tr 
                                                                    key={v.id}
                                                                    draggable="true"
                                                                    onDragStart={(e) => handleDragStart(e, vIdx)}
                                                                    onDragOver={(e) => handleDragOver(e, vIdx)}
                                                                    onDrop={(e) => handleDrop(e, vIdx)}
                                                                    onDragEnd={() => { setDraggedIdx(null); setDragOverIdx(null) }}
                                                                    style={{
                                                                        opacity: isDragged ? 0.4 : 1,
                                                                        backgroundColor: isDragOver ? '#f1f0f6' : undefined,
                                                                        transition: 'background-color 0.2s ease, opacity 0.2s ease'
                                                                    }}
                                                                >
                                                                    <td style={{ width: '40px', textAlign: 'center', color: '#aaa', cursor: 'grab', userSelect: 'none' }} title="Drag to reorder">☰</td>
                                                                    <td>
                                                                        {editingValue?.id === v.id ? (
                                                                            <Form.Control size="sm" value={editValueText} onChange={e => setEditValueText(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEditValue()} autoFocus />
                                                                        ) : v.value}
                                                                    </td>
                                                                    {selectedLookupTable?.slug === 'end_connections' && (
                                                                        <td>
                                                                            <Form.Control
                                                                                size="sm"
                                                                                value={endConnDetails[v.id]?.type || ''}
                                                                                onChange={e => updateEndConnDetail(v.id, 'type', e.target.value)}
                                                                                placeholder="e.g. 37° flare"
                                                                                style={{ fontSize: '0.8rem' }}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    {selectedLookupTable?.slug === 'end_connections' && (
                                                                        <td>
                                                                            <Form.Control
                                                                                size="sm"
                                                                                value={endConnDetails[v.id]?.typical_use || ''}
                                                                                onChange={e => updateEndConnDetail(v.id, 'typical_use', e.target.value)}
                                                                                placeholder="e.g. High-pressure ground systems"
                                                                                style={{ fontSize: '0.8rem' }}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    {selectedLookupTable?.slug === 'end_connections' && (
                                                                        <td>
                                                                            <Form.Select
                                                                                size="sm"
                                                                                value={endConnDetails[v.id]?.status || 'Standard'}
                                                                                onChange={e => updateEndConnDetail(v.id, 'status', e.target.value)}
                                                                                style={{ fontSize: '0.8rem' }}
                                                                            >
                                                                                <option value="Standard">Standard</option>
                                                                                <option value="On request">On request</option>
                                                                                <option value="Deprecated">Deprecated</option>
                                                                                <option value="Legacy">Legacy</option>
                                                                            </Form.Select>
                                                                        </td>
                                                                    )}
                                                                    {selectedLookupTable?.slug === 'seal_material' && (
                                                                        <td>
                                                                            <Form.Control
                                                                                size="sm"
                                                                                value={sealMatDetails[v.id]?.min_temp || ''}
                                                                                onChange={e => updateSealMatDetail(v.id, 'min_temp', e.target.value)}
                                                                                placeholder="e.g. -350°F"
                                                                                style={{ fontSize: '0.8rem' }}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    {selectedLookupTable?.slug === 'seal_material' && (
                                                                        <td>
                                                                            <Form.Control
                                                                                size="sm"
                                                                                value={sealMatDetails[v.id]?.max_temp || ''}
                                                                                onChange={e => updateSealMatDetail(v.id, 'max_temp', e.target.value)}
                                                                                placeholder="e.g. +450°F"
                                                                                style={{ fontSize: '0.8rem' }}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    <td style={{ fontSize: '0.8rem', color: '#888' }}>{v.sort_order}</td>
                                                                    <td>
                                                                        {editingValue?.id === v.id ? (
                                                                            <>
                                                                                <Button size="sm" variant="outline-success" onClick={saveEditValue} style={{ marginRight: 4 }}>✓</Button>
                                                                                <Button size="sm" variant="outline-secondary" onClick={() => setEditingValue(null)}>✕</Button>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {vIdx > 0 && <Button size="sm" variant="outline-secondary" onClick={() => moveValue(v.id, 'up')} style={{ marginRight: 2, padding: '0 4px' }}>↑</Button>}
                                                                                {vIdx < tableValues.length - 1 && <Button size="sm" variant="outline-secondary" onClick={() => moveValue(v.id, 'down')} style={{ marginRight: 4, padding: '0 4px' }}>↓</Button>}
                                                                                <Button size="sm" variant="outline-primary" onClick={() => { setEditingValue(v); setEditValueText(v.value) }} style={{ marginRight: 4 }}>Edit</Button>
                                                                                <Button size="sm" variant="outline-danger" onClick={() => deleteValue(v.id)}>×</Button>
                                                                            </>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </Table>
                                                {tableValues.length === 0 && <p style={{ color: '#888', fontSize: '0.85rem' }}>No values yet. Add some above.</p>}
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', minHeight: '300px' }}>
                                                Select a table from the left to manage its values.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Tab>

                            <Tab eventKey="cat-attributes" title="Category Attributes">
                                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1.5rem' }}>
                                    Select which lookup tables are shown when editing a product in each category and in the public Product Configurator for customers. Only the assigned tables will appear as configuration steps and product attribute checkboxes.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '1.5rem', minHeight: '400px' }}>
                                    {/* Left: category list */}
                                    <div>
                                        <strong style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.75rem' }}>Categories</strong>
                                        <div className="pfc-admin-side-list">
                                            {categories.map(c => (
                                                <div
                                                    key={c.id}
                                                    onClick={() => setSelectedCatForMapping(c)}
                                                    className={`pfc-admin-side-item ${selectedCatForMapping?.id === c.id ? 'active' : ''}`}
                                                >
                                                    <span>{c.name}</span>
                                                    <span style={{ fontSize: '0.7rem', color: '#888' }}>
                                                        {(categoryLookupMappings[c.id] || []).length} tables
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Right: lookup table checkboxes for selected category */}
                                    <div>
                                        {selectedCatForMapping ? (
                                            <>
                                                <h5 style={{ fontSize: '1.1rem', marginBottom: '0.25rem', color: '#26215C', fontWeight: 600 }}>{selectedCatForMapping.name}</h5>
                                                <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1.25rem' }}>
                                                    Check the lookup tables that apply to products in this category.
                                                </p>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                                    {(Array.isArray(lookupTables) ? lookupTables : []).map(t => {
                                                        const checked = (categoryLookupMappings[selectedCatForMapping.id] || []).includes(t.id)
                                                        const toggle = async () => {
                                                            const current = categoryLookupMappings[selectedCatForMapping.id] || []
                                                            const updated = checked ? current.filter(id => id !== t.id) : [...current, t.id]
                                                            setCategoryLookupMappings(prev => ({ ...prev, [selectedCatForMapping.id]: updated }))
                                                            await axios.put(`${API}/api/category-lookup-tables/${selectedCatForMapping.id}`, { lookup_table_ids: updated })
                                                        }
                                                        return (
                                                            <label key={t.id} className={`pfc-checkbox-card ${checked ? 'checked' : ''}`}>
                                                                <input type="checkbox" checked={checked} onChange={toggle} style={{ margin: 0 }} />
                                                                {t.label}
                                                            </label>
                                                        )
                                                    })}
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888', minHeight: '300px' }}>
                                                Select a category from the left to assign lookup tables.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Tab>

                            <Tab eventKey="custom-families" title="Custom Families">
                                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                                    Manage the family cards shown on the Custom Products page (/products/custom). Icon should be a Tabler icon class name (e.g. <code>ti-circle-dot</code>) matching one already used on the site.
                                </p>
                                <Button size="sm" onClick={openAddFamily} className="pfc-admin-btn-primary" style={{ marginBottom: '1.5rem', padding: '6px 16px', fontWeight: 500 }}>+ Add Family</Button>
                                <Table bordered hover responsive className="pfc-admin-table">
                                    <thead><tr><th>Icon</th><th>Name</th><th>Description</th><th style={{ width: '90px' }}>Sort</th><th style={{ width: '160px' }}>Actions</th></tr></thead>
                                    <tbody>
                                        {customFamilies.map(f => (
                                            <tr key={f.id}>
                                                <td><i className={`ti ${f.icon}`} style={{ fontSize: '1.25rem', color: '#26215C' }}></i></td>
                                                <td>{f.name}</td>
                                                <td style={{ fontSize: '0.85rem', color: '#666' }}>{f.description}</td>
                                                <td style={{ fontSize: '0.85rem', color: '#888' }}>{f.sort_order || 0}</td>
                                                <td>
                                                    <Button size="sm" variant="outline-primary" onClick={() => openEditFamily(f)} style={{ marginRight: '0.5rem' }}>Edit</Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => { setDeleteTarget({ type: 'custom-family', id: f.id, name: f.name }); setShowDelete(true) }}>Del</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Tab>
                        </Tabs>
                    </div>

                    {/* ── Product Modal ── */}
                    <Modal show={showProductModal} onHide={() => setShowProductModal(false)} centered size="lg" scrollable>
                        <Modal.Header closeButton>
                            <Modal.Title>{editProduct ? 'Edit Product' : 'Add Product'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {/* Fields grid with inline lookup checkboxes */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem' }}>
                                {editableCols.map(col => {
                                    const lookupSlug = FIELD_TO_LOOKUP[col]
                                    const lookupGroup = lookupSlug ? lookupValuesByTable[lookupSlug] : null
                                    const hasLookup = lookupGroup && lookupGroup.values.length > 0

                                    return (
                                        <Form.Group key={col} style={col === 'description' ? { gridColumn: '1 / -1' } : undefined}>
                                            <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{colLabel(col)}</Form.Label>
                                            {col === CATEGORY_COL ? (
                                                <Form.Select size="sm" value={productForm[col] || ''} onChange={e => setField(col, e.target.value)}>
                                                    <option value="">None</option>
                                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </Form.Select>
                                            ) : col === 'description' ? (
                                                <Form.Control as="textarea" size="sm" value={productForm[col] || ''} onChange={e => setField(col, e.target.value)} style={{ overflow: 'hidden', resize: 'none', minHeight: '4.5rem' }} onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }} />
                                            ) : (
                                                <Form.Control as="textarea" rows={1} size="sm" value={productForm[col] || ''} onChange={e => setField(col, e.target.value)} style={{ overflow: 'hidden', resize: 'none', minHeight: '2rem' }} onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }} />
                                            )}
                                            {/* Inline lookup checkboxes below the field */}
                                            {hasLookup && (
                                                <div style={{ marginTop: '6px', padding: '8px 12px', backgroundColor: '#faf9f6', border: '1px solid #e5e3dd', borderRadius: '6px' }}>
                                                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#26215C', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        {lookupGroup.table.label}
                                                    </div>
                                                    <div className="pfc-spec-chip-container">
                                                        {lookupGroup.values.map(v => {
                                                            const isSelected = productLookupSelections.includes(v.id)
                                                            return (
                                                                <label key={v.id} className={`pfc-spec-chip ${isSelected ? 'active' : ''}`}>
                                                                    <input type="checkbox" checked={isSelected} onChange={() => toggleLookupValue(v.id)} />
                                                                    {isSelected && <span style={{ marginRight: '2px' }}>✓</span>}
                                                                    {v.value}
                                                                </label>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    )
                                })}
                            </div>

                            {/* Lookup tables with no visible legacy field */}
                            {(() => {
                                const visibleMappedSlugs = new Set(
                                    editableCols
                                        .filter(c => FIELD_TO_LOOKUP[c])
                                        .map(c => FIELD_TO_LOOKUP[c])
                                )
                                const unmapped = Object.entries(lookupValuesByTable).filter(([slug, { values }]) => !visibleMappedSlugs.has(slug) && values.length > 0)
                                if (unmapped.length === 0) return null
                                return (
                                    <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e3dd', paddingTop: '1rem' }}>
                                        <h6 style={{ fontFamily: 'var(--pfc-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#26215C', marginBottom: '0.75rem', fontSize: '0.85rem' }}>Other Specs</h6>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                            {unmapped.map(([slug, { table, values }]) => (
                                                <div key={slug} style={{ padding: '8px 12px', backgroundColor: '#faf9f6', border: '1px solid #e5e3dd', borderRadius: '6px' }}>
                                                    <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#26215C', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        {table.label}
                                                    </div>
                                                    <div className="pfc-spec-chip-container">
                                                        {values.map(v => {
                                                            const isSelected = productLookupSelections.includes(v.id)
                                                            return (
                                                                <label key={v.id} className={`pfc-spec-chip ${isSelected ? 'active' : ''}`}>
                                                                    <input type="checkbox" checked={isSelected} onChange={() => toggleLookupValue(v.id)} />
                                                                    {isSelected && <span style={{ marginRight: '2px' }}>✓</span>}
                                                                    {v.value}
                                                                </label>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })()}
                                  {/* ── Image Management (only when editing existing product) ── */}
                            {editProduct && (
                                <div style={{ marginTop: '2rem', borderTop: '1px solid #e5e3dd', paddingTop: '1.5rem' }}>
                                    <h6 style={{ fontFamily: 'var(--pfc-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#26215C', marginBottom: '1rem', fontSize: '0.85rem' }}>Product Images</h6>

                                    {/* Current images */}
                                    {productImages.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '1.25rem' }}>
                                            {productImages.map((img, idx) => (
                                                <div key={img.id} style={{ position: 'relative', border: '1px solid #e5e3dd', borderRadius: '6px', overflow: 'hidden', width: '120px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', transition: 'transform 0.2s ease' }} className="pfc-image-tile">
                                                    <img
                                                        src={`${API}/products/images/product/${img.filename}`}
                                                        alt={img.filename}
                                                        style={{ width: '100%', height: '90px', objectFit: 'contain', backgroundColor: '#fcfbfa', display: 'block' }}
                                                    />
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: '#faf9f6', borderTop: '1px solid #e5e3dd' }}>
                                                        <div>
                                                            {idx > 0 && (
                                                                <button onClick={() => moveImage(img.id, 'up')} title="Move left" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '0 4px', color: '#555' }}>◀</button>
                                                            )}
                                                            {idx < productImages.length - 1 && (
                                                                <button onClick={() => moveImage(img.id, 'down')} title="Move right" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '0 4px', color: '#555' }}>▶</button>
                                                            )}
                                                        </div>
                                                        <button onClick={() => deleteImage(img.id)} title="Delete image" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '0.8rem', padding: '0 4px', fontWeight: 'bold' }}>✕</button>
                                                    </div>
                                                    {idx === 0 && <span style={{ position: 'absolute', top: '4px', left: '4px', backgroundColor: '#26215C', color: '#fff', fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Primary</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Upload box */}
                                    <div className="pfc-upload-zone">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleUpload}
                                        />
                                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>📤</div>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#26215C' }}>
                                            {uploading ? 'Uploading files...' : 'Click or Drag images here to upload'}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                            Supports PNG, JPG, GIF. First image will be set as primary.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Attachment Management (only when editing existing product) ── */}
                            {editProduct && (
                                <div style={{ marginTop: '2rem', borderTop: '1px solid #e5e3dd', paddingTop: '1.5rem' }}>
                                    <h6 style={{ fontFamily: 'var(--pfc-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#26215C', marginBottom: '1rem', fontSize: '0.85rem' }}>Attachments & Downloads</h6>

                                    {productAttachments.length > 0 && (
                                        <div style={{ marginBottom: '1.25rem' }}>
                                            {productAttachments.map(a => (
                                                <div key={a.id} className="pfc-file-item">
                                                    <a href={`${API}/products/${a.url}`} target="_blank" rel="noopener noreferrer" className="pfc-file-name">
                                                        <span>📄</span>
                                                        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{a.name}</span>
                                                    </a>
                                                    <button onClick={() => deleteAttachment(a.id)} title="Delete attachment" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '0.85rem', fontWeight: 'bold', padding: '2px 8px' }}>✕</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="pfc-upload-zone">
                                        <input
                                            ref={attachmentInputRef}
                                            type="file"
                                            multiple
                                            onChange={handleAttachmentUpload}
                                        />
                                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>📎</div>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#26215C' }}>
                                            {uploadingAttachment ? 'Uploading files...' : 'Click or Drag files here to upload'}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                            Upload PDFs, technical datasheets, STEP files, and manuals.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!editProduct && (
                                <p style={{ marginTop: '1.5rem', color: '#888', fontSize: '0.85rem', fontStyle: 'italic' }}>
                                    Save the product first, then edit it to add images.
                                </p>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowProductModal(false)}>Cancel</Button>
                            <Button className="pfc-admin-btn-primary" onClick={saveProduct}>Save</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ── Category Modal ── */}
                    <Modal show={showCatModal} onHide={() => setShowCatModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{editCat ? 'Edit Category' : 'Add Category'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} /></Form.Group>
                            <Form.Group className="mb-3"><Form.Label>Parent Category</Form.Label>
                                <Form.Select value={catForm.parent_id} onChange={e => setCatForm({ ...catForm, parent_id: e.target.value })}>
                                    <option value="">None (top-level)</option>
                                    {categories.filter(c => !editCat || c.id !== editCat.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Icon (Tabler icon class, e.g. ti-settings)</Form.Label>
                                <Form.Control value={catForm.icon} onChange={e => setCatForm({ ...catForm, icon: e.target.value })} placeholder="ti-settings" />
                                <Form.Text muted>Leave blank to show the numbered badge instead. Browse names at tabler-icons.io.</Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control as="textarea" rows={3} value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} />
                                <Form.Text muted>Shown on the Home page card, between the category name and "View products →". Leave blank to hide.</Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Sort Order</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={catForm.sort_order}
                                    onChange={e => setCatForm({ ...catForm, sort_order: e.target.value })}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowCatModal(false)}>Cancel</Button>
                            <Button className="pfc-admin-btn-primary" onClick={saveCat}>Save</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ── Custom Family Modal ── */}
                    <Modal show={showFamilyModal} onHide={() => setShowFamilyModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{editFamily ? 'Edit Custom Family' : 'Add Custom Family'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control value={familyForm.name} onChange={e => setFamilyForm({ ...familyForm, name: e.target.value })} /></Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Icon (Tabler icon class, e.g. ti-circle-dot)</Form.Label>
                                <Form.Control value={familyForm.icon} onChange={e => setFamilyForm({ ...familyForm, icon: e.target.value })} placeholder="ti-circle-dot" />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Icon Accent Color</Form.Label>
                                <Form.Select value={familyForm.icon_class} onChange={e => setFamilyForm({ ...familyForm, icon_class: e.target.value })}>
                                    <option value="cc-icon-p">Plum</option>
                                    <option value="cc-icon-a">Amber</option>
                                    <option value="cc-icon-t">Teal</option>
                                    <option value="cc-icon-b">Blue</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control as="textarea" rows={3} value={familyForm.description} onChange={e => setFamilyForm({ ...familyForm, description: e.target.value })} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Sort Order</Form.Label>
                                <Form.Control type="number" value={familyForm.sort_order} onChange={e => setFamilyForm({ ...familyForm, sort_order: e.target.value })} />
                            </Form.Group>

                            {/* ── Slideshow Image Management (only when editing an existing family) ── */}
                            {editFamily && (
                                <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e3dd', paddingTop: '1.25rem' }}>
                                    <h6 style={{ fontFamily: 'var(--pfc-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#26215C', marginBottom: '1rem', fontSize: '0.85rem' }}>Slideshow Images</h6>

                                    {familyImages.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '1.25rem' }}>
                                            {familyImages.map((img, idx) => (
                                                <div key={img.id} style={{ position: 'relative', border: '1px solid #e5e3dd', borderRadius: '6px', overflow: 'hidden', width: '110px' }}>
                                                    <img
                                                        src={`${API}${img.image_path}`}
                                                        alt={familyForm.name}
                                                        style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }}
                                                    />
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: '#faf9f6', borderTop: '1px solid #e5e3dd' }}>
                                                        <div>
                                                            {idx > 0 && (
                                                                <button type="button" onClick={() => moveFamilyImg(img.id, 'up')} title="Move left" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '0 4px', color: '#555' }}>◀</button>
                                                            )}
                                                            {idx < familyImages.length - 1 && (
                                                                <button type="button" onClick={() => moveFamilyImg(img.id, 'down')} title="Move right" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '0 4px', color: '#555' }}>▶</button>
                                                            )}
                                                        </div>
                                                        <button type="button" onClick={() => deleteFamilyImg(img.id)} title="Delete image" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '0.8rem', padding: '0 4px', fontWeight: 'bold' }}>✕</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="pfc-upload-zone">
                                        <input
                                            ref={familyFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFamilyImgUpload}
                                        />
                                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>📤</div>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#26215C' }}>
                                            {uploadingFamilyImg ? 'Uploading images...' : 'Click or Drag images here to upload'}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                            Shown as the slideshow when a visitor clicks this family card.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowFamilyModal(false)}>Cancel</Button>
                            <Button className="pfc-admin-btn-primary" onClick={saveFamily}>Save</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* ── Delete Confirmation ── */}
                    <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title style={{ color: '#dc3545' }}>Confirm Delete</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</p>
                            {deleteTarget?.type === 'product' && <p style={{ color: '#dc3545', fontSize: '0.875rem' }}>This will also delete all specifications, attachments, and images for this product.</p>}
                            {deleteTarget?.type === 'category' && <p style={{ color: '#dc3545', fontSize: '0.875rem' }}>Products in this category will become uncategorized.</p>}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDelete(false)}>Cancel</Button>
                            <Button variant="danger" onClick={confirmDelete}>Delete</Button>
                        </Modal.Footer>
                    </Modal>
                </Container>
            </section>
        </V2Layout>
    )
}

export default ProductsAdminScreen
